import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { FaRupeeSign, FaDownload, FaRedo } from 'react-icons/fa';
import { calculateRD, formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';
import { isPrerender } from '../utils/prerender';

ChartJS.register(ArcElement, Tooltip, Legend);

const RDCalculator = () => {
  const shouldRenderChart = !isPrerender();
  const [monthlyDeposit, setMonthlyDeposit] = useState(10000);
  const [rate, setRate] = useState(7);
  const [tenure, setTenure] = useState(24);
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [monthlyDeposit, rate, tenure]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const m = Number(monthlyDeposit) || 0;
    const r = Number(rate) || 0;
    const t = Number(tenure) || 0;
    const rdResult = calculateRD(m, r, t);
    setResult(rdResult);
  };

  const handleDownloadPDF = () => {
    generateGenericPDF('RD Calculator Report', {
      'Monthly Deposit': monthlyDeposit,
      'Interest Rate': `${rate}% p.a.`,
      'Tenure': `${tenure} months`,
      'Total Invested': result.totalInvested,
      'Total Interest': result.totalInterest,
      'Maturity Amount': result.maturityAmount,
    });
  };

  const handleReset = () => {
    setMonthlyDeposit(10000);
    setRate(7);
    setTenure(24);
  };

  const chartData = result ? {
    labels: ['Total Deposits', 'Interest Earned'],
    datasets: [{
      data: [result.totalInvested, result.totalInterest],
      backgroundColor: ['#6366f1', '#10b981'],
      borderColor: ['#4f46e5', '#059669'],
      borderWidth: 2,
    }],
  } : null;

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="RD Calculator"
        description="Free RD Calculator - Calculate Recurring Deposit maturity amount. Monthly savings growth with compound interest."
        keywords="RD calculator, recurring deposit calculator, RD maturity calculator, RD interest calculator, monthly deposit calculator"
        canonicalPath="/rd"
      />
      <div className="calculator-header">
        <h1>RD Calculator</h1>
        <p>Calculate Recurring Deposit returns for regular monthly savings</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Monthly Deposit (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={monthlyDeposit === '' ? '' : monthlyDeposit}
                onChange={(e) => setMonthlyDeposit(e.target.value === '' ? '' : Number(e.target.value))}
                min="100"
                max="500000"
              />
            </div>
            <div className="slider-container">
              <input
                type="range"
                min="100"
                max="100000"
                step="100"
                value={monthlyDeposit || 0}
                onChange={(e) => setMonthlyDeposit(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((monthlyDeposit || 0) / 100000) * 100}%` }}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Interest Rate (% p.a.)</label>
            <input
              type="number"
              value={rate === '' ? '' : rate}
              onChange={(e) => setRate(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="12"
              step="0.1"
            />
            <div className="slider-container">
              <input
                type="range"
                min="1"
                max="12"
                step="0.1"
                value={rate || 0}
                onChange={(e) => setRate(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((rate || 0) / 12) * 100}%` }}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Tenure (Months)</label>
            <input
              type="number"
              value={tenure === '' ? '' : tenure}
              onChange={(e) => setTenure(e.target.value === '' ? '' : Number(e.target.value))}
              min="6"
              max="120"
            />
            <div className="slider-container">
              <input
                type="range"
                min="6"
                max="120"
                step="6"
                value={tenure || 0}
                onChange={(e) => setTenure(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((tenure || 0) / 120) * 100}%` }}
              />
            </div>
            <small style={{ color: 'var(--text-secondary)' }}>
              {Math.floor(tenure / 12)} years {tenure % 12} months
            </small>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary">
                <h3>Maturity Amount</h3>
                <div className="amount">{formatCurrency(result.maturityAmount)}</div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Total Deposits</span>
                  <span className="value">{formatCurrency(result.totalInvested)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Interest</span>
                  <span className="value" style={{ color: '#10b981' }}>
                    +{formatCurrency(result.totalInterest)}
                  </span>
                </div>
                <div className="result-item">
                  <span className="label">Interest Percentage</span>
                  <span className="value">
                    {((result.totalInterest / result.totalInvested) * 100).toFixed(2)}%
                  </span>
                </div>
              </div>

              {shouldRenderChart && (
                <div className="chart-container">
                  <Pie data={chartData} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>About Recurring Deposits</h4>
        <ul>
          <li><strong>Regular Savings:</strong> Ideal for building a savings habit</li>
          <li><strong>Compounding:</strong> Interest is compounded quarterly</li>
          <li><strong>Flexibility:</strong> Tenure ranges from 6 months to 10 years</li>
          <li><strong>Minimum Amount:</strong> Usually starts from ₹100/month</li>
          <li><strong>Premature Withdrawal:</strong> Allowed with penalty in most banks</li>
        </ul>
      </div>
    </div>
  );
};

export default RDCalculator;
